/**
 * Jul-2026 (User-Wunsch) — Täglicher Bestell-Hinweis:
 * Erscheint beim ersten Login des Tages (Morgen-Fenster), um 13:00 und um
 * 16:30 (vor Feierabend), wenn es offene Einträge in der Bestellliste oder
 * Artikel unter Mindestbestand gibt. Pro Fenster einmal wegklickbar.
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ShoppingCart, X } from "lucide-react";
import api from "../../lib/api";

function currentWindow() {
    const now = new Date();
    const mins = now.getHours() * 60 + now.getMinutes();
    if (mins >= 16 * 60 + 30) return "abend";
    if (mins >= 13 * 60) return "mittag";
    return "morgen";
}

const WINDOW_LABEL = {
    morgen: "Guten Morgen",
    mittag: "Mittags-Erinnerung",
    abend: "Vor Feierabend",
};

export const ReorderReminderBanner = () => {
    const navigate = useNavigate();
    const [status, setStatus] = useState(null);
    const [win, setWin] = useState(currentWindow());
    const [, setTick] = useState(0);

    useEffect(() => {
        api.get("/wms/reorder-status").then((r) => setStatus(r.data)).catch(() => {});
        const t = setInterval(() => setWin(currentWindow()), 60000);
        return () => clearInterval(t);
    }, []);

    if (!status) return null;
    const openList = Number(status.open_reorder_count || 0);
    const lowStock = Number(status.low_stock_count || 0);
    const drafts = Number(status.draft_po_count || 0);
    if (!openList && !lowStock && !drafts) return null;

    const key = `reorderHint:${new Date().toISOString().slice(0, 10)}:${win}`;
    if (localStorage.getItem(key)) return null;

    const parts = [];
    if (openList) parts.push(`${openList} offene Position(en) in der Bestellliste`);
    if (lowStock) parts.push(`${lowStock} Artikel unter Mindestbestand`);
    if (drafts) parts.push(`${drafts} nicht gesendete Bestellung(en)`);

    return (
        <div className="bg-amber-50 border border-amber-400 rounded-sm px-4 py-3 mb-4 flex items-center gap-3 flex-wrap" data-testid="reorder-reminder-banner">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
            <div className="flex-1 min-w-[200px]">
                <div className="text-xs font-bold uppercase tracking-wider text-amber-800">{WINDOW_LABEL[win]} — Bestell-Hinweis</div>
                <div className="text-sm text-gray-800 truncate max-w-full">{parts.join(" · ")} — bitte bestellen.</div>
            </div>
            <button
                onClick={() => navigate("/dealer/wms?tab=reorder")}
                className="inline-flex items-center gap-1.5 bg-[#111827] text-white text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-sm hover:bg-black"
                data-testid="reorder-reminder-open-list"
            >
                <ShoppingCart className="w-3.5 h-3.5" /> Bestellliste
            </button>
            <button
                onClick={() => navigate("/dealer/wms?low=1")}
                className="text-xs font-bold uppercase tracking-wider border border-amber-500 text-amber-800 px-3 py-2 rounded-sm hover:bg-amber-100"
                data-testid="reorder-reminder-open-lowstock"
            >
                Mindestbestand
            </button>
            <button
                onClick={() => navigate("/dealer/purchase-orders")}
                className="text-xs font-bold uppercase tracking-wider border border-amber-500 text-amber-800 px-3 py-2 rounded-sm hover:bg-amber-100"
                data-testid="reorder-reminder-open-pos"
            >
                Bestellungen
            </button>
            <button
                onClick={() => { localStorage.setItem(key, "1"); setTick((t) => t + 1); }}
                className="text-amber-700 hover:text-amber-900 p-1"
                title="Für dieses Zeitfenster ausblenden"
                data-testid="reorder-reminder-dismiss"
            >
                <X className="w-4 h-4" />
            </button>
        </div>
    );
};
